import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useRegisterDataIntegrationStepControls } from 'src/features/Signup/hooks/useRegisterDataIntegrationStepControls';
import StartModelingStep from 'src/features/Signup/components/templates/DataIntegrationFlow/steps/StartModelingStep';
import {
  getStepIndex,
  getNextStepPath,
  getPrevStepPath,
} from 'src/features/Signup/utils/dataIntegrationSteps';
import { modelsQueryKey, useModelsQuery } from 'src/api/models';
import { useAppContext } from 'src/contexts/AppContext';

const nextPath = getNextStepPath('start-modeling');
const prevPath = getPrevStepPath('start-modeling');

export const StartModelingStepRoute: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentCustomer } = useAppContext();
  const [startedThisSession, setStartedThisSession] = useState(false);
  const customerId = currentCustomer?.customerName ?? '';

  const handleModelingStarted = () => {
    setStartedThisSession(true);
    // The models list is cached with a long staleTime — without invalidation
    // a remount of this route would read the stale (empty) list and show the
    // form again
    queryClient.invalidateQueries(modelsQueryKey(customerId));
  };

  // Server-driven, like the data-upload step: models existing on the backend
  // mean modeling was already started (possibly in a previous session)
  const { data: models = [], isLoading: isCheckingModels } =
    useModelsQuery(customerId);
  const modelingRan = startedThisSession || models.length > 0;

  useRegisterDataIntegrationStepControls({
    title: 'Start modeling',
    subtitle: 'Review KPI and marketing data before starting modeling',
    currentStep: getStepIndex('start-modeling'),
    onNext: nextPath ? () => navigate(nextPath) : undefined,
    onBack: prevPath ? () => navigate(prevPath) : undefined,
    // Once modeling has started, the earlier data steps must not be revisited
    isBackDisabled: modelingRan,
    isNextDisabled: false,
    // Modeling is optional — make skipping it explicit
    nextLabel: !isCheckingModels && !modelingRan ? 'Skip' : undefined,
  });

  return (
    <StartModelingStep
      modelingRan={modelingRan}
      isCheckingModels={isCheckingModels}
      onModelingStarted={handleModelingStarted}
    />
  );
};
